import React from 'react';

import './MainSectionPreviewVideo.scss';
import { Project } from '../../../types/Project';
import { OverlayOrientationEnum } from '../../../types/enums/OverlayOrientationEnum';
import { OverlayStyleEnum } from '../../../types/enums/OverlayStyleEnum';
import { OverlayFontEnum } from '../../../types/enums/OverlayFontEnum';
import PreviewLogo from '../../../images/accessloop-video-overlay-logo.png';
import { CaptionModeEnum } from '../../../types/enums/CaptionModeEnum';

type MainSectionPreviewVideoProps = {
  project?: Project;
};

const MainSectionPreviewVideo = ({ project }: MainSectionPreviewVideoProps) => {

  // const divRef = useRef<HTMLDivElement>(null);
  // const [width, setWidth] = useState(0);

  const toRgaColor = (color: string | undefined, transparency: number | undefined) => {
    if (!color) {
      color = '#FFFFFF';
    }

    if (!transparency) {
      transparency = 0.75;
    }

    color = color.replace('#', '');

    const r = parseInt(color?.substring(0, 2) || '0', 16);
    const g = parseInt(color?.substring(2, 4) || '0', 16);
    const b = parseInt(color?.substring(4, 6) || '0', 16);
    const a = transparency || 0.75;

    return `rgba(${r}, ${g}, ${b}, ${a})`;
  };

  const getOverlayBackgroundColor = () => {
    const transparency = project?.encoder?.overlayBackgroundTransparency;
    const color = project?.encoder?.overlayBackgroundColour;

    return toRgaColor(color, transparency);
  };

  const getOverlayFontColor = () => {
    const transparency = project?.encoder?.overlayFontTransparency;
    const color = project?.encoder?.overlayFontColour;

    return toRgaColor(color, transparency);
  };

  const getOverlayPadding = () => {
    const vertical = project?.encoder?.overlayVerticalPadding;
    const horizontal = project?.encoder?.overlayHorizontalPadding;

    return `${vertical}px ${horizontal}px ${vertical}px ${horizontal}px`;
  };

  const getOverlayMargin = () => {
    if (project?.encoder.overlayStyle !== OverlayStyleEnum.Letterbox) {
      return `0px 0px 0px 0px`;
    }

    const offset = project?.encoder?.overlayVerticalOffset;

    switch (project?.encoder?.overlayOrientation) {
      default:
      case OverlayOrientationEnum.Bottom:
        return `0px 0px ${offset}px 0px`;
      case OverlayOrientationEnum.Top:
        return `${offset}px 0px 0px 0px`;
      case OverlayOrientationEnum.Middle:
        return `0px 0px 0px 0px`;
    }
  };

  const getFontViewHeight = () => {
    const lineHeight = project?.encoder?.overlayLineSpacing ?? 5;
    const lines = project?.encoder?.aggregatorMaxLines ?? 2;
    const fontSize = project?.encoder?.overlayFontSize ?? 37;

    return `${lines * (fontSize + lineHeight)}px`;
  }

  const getOverlayFontSize = () => {
    return `${project?.encoder?.overlayFontSize}px`;
  };

  const getOverlayFontFamily = () => {
    switch(project?.encoder?.overlayFont) {
      case OverlayFontEnum.Arial:
        return `Arial`;
      case OverlayFontEnum.Tahoma:
        return `Tahoma`;
      case OverlayFontEnum.TimesNewRoman:
        return `Times`;
      default:
      case OverlayFontEnum.Verdana:
        return `Verdana`;
    }
  };

  const getOverlayLineHeight = () => {
    return `${
      (project?.encoder?.overlayFontSize ?? 37) + (project?.encoder?.overlayLineSpacing ?? 5)
    }px`;
  };

  const getOverlayHeight = () => {
    const lineHeight = project?.encoder?.overlayLineSpacing ?? 5;
    const lines = project?.encoder?.aggregatorMaxLines ?? 2;
    const fontSize = project?.encoder?.overlayFontSize ?? 37;
    const height =
      lines * fontSize + lineHeight * lines + ((project?.encoder?.overlayVerticalPadding ?? 10) * 2);

    return `${height}px`;
  };

  const getOverlayWidth = () => {
    switch (project?.encoder?.overlayStyle) {
      default:
      case OverlayStyleEnum.FullScreen:
        return `100%`;
      case OverlayStyleEnum.Letterbox:
        return `${Math.ceil(1920 / 2)}px`;
    }
  };

  const getOverlayTop = () => {
    switch (project?.encoder?.overlayOrientation) {
      default:
      case OverlayOrientationEnum.Bottom:
        return 'auto';
      case OverlayOrientationEnum.Top:
        return `${project?.encoder?.overlayVerticalOffset}px`;
      case OverlayOrientationEnum.Middle:
        const height = parseInt(getOverlayHeight().replace('px', ''));

        return `${1080 / 2 - height / 2}px`;
    }
  };

  const getOverlayBottom = () => {
    switch (project?.encoder?.overlayOrientation) {
      default:
      case OverlayOrientationEnum.Bottom:
        return`${project?.encoder?.overlayVerticalOffset}px`;
      case OverlayOrientationEnum.Top:
      case OverlayOrientationEnum.Middle:
        return `auto`;
    }
  };

  const getOverlayLeft = () => {
    switch (project?.encoder?.overlayStyle) {
      default:
      case OverlayStyleEnum.FullScreen:
        return 'auto';
      case OverlayStyleEnum.Letterbox:
        return `${Math.ceil(1920 / 2 / 2)}px`;
    }
  };

  // useEffect(() => {
  //   const updateWidth = () => {
  //     if (divRef.current) {
  //       setWidth(divRef.current.getBoundingClientRect().width);
  //     }
  //   };

  //   updateWidth();
  //   window.addEventListener("resize", updateWidth);

  //   return () => window.removeEventListener("resize", updateWidth);
  // }, []);

  return (
    <div className="main-section-preview-video">
      <div className="main-section-preview-video-logo"><img src={PreviewLogo} alt="AccessLOOP" /></div>

      {project?.encoder.captionMode !== CaptionModeEnum.None && (
        <>
          <div
            style={{
              backgroundColor: getOverlayBackgroundColor(),
              color: getOverlayFontColor(),
              padding: getOverlayPadding(),
              margin: getOverlayMargin(),
              fontSize: getOverlayFontSize(),
              fontFamily: getOverlayFontFamily(),
              lineHeight: getOverlayLineHeight(),
              height: `${getOverlayHeight()}`,
              width: `${getOverlayWidth()}`,
              transition: 'all 0.3s ease-in-out',
              overflow: 'hidden',
              position: 'absolute',
              top: getOverlayTop(),
              bottom: getOverlayBottom(),
              left: getOverlayLeft(),
            }}
          >
            <div
              style={{height: getFontViewHeight(), overflow: 'hidden'}}>
              This text is to demonstrate how the captions will appear based on the styling you have
              selected in the control panel. Welcome to AccessLOOP, our mission is to make the world of
              live streaming accessible to all. Add accessibility easily to all livestreamed and virtual
              events.
            </div>
          </div>
        </>        
      )}
    </div>
  );
};

export default MainSectionPreviewVideo;
